import type { PaletteObject, PaletteObjectId } from '@/types/object';

/**
 * Palette library. Every entry ports 1:1 from the legacy index.html —
 * dims, sabins, icon, placement, and color coding are preserved so the
 * React viewport can reproduce the original scene without drift.
 *
 * Sabins are ABSOLUTE absorption per frequency band (already includes
 * the object's area). They are summed directly into the RT60 numerator.
 */
export const PALETTE_OBJECTS: readonly PaletteObject[] = [
  /* ─── Sound sources ──────────────────────────────────────────────── */
  {
    id: 'studio-monitors',
    label: 'Wall Monitors',
    icon: 'speaker',
    sabins: [0, 0, 0, 0, 0, 0],
    dims: [0.2, 0.2, 0.3],
    color: 0x2a2a40,
    wireColor: 0x709424,
    placement: 'wall',
    isMonitor: true,
  },
  {
    id: 'monitor-stands',
    label: 'Monitor Stands',
    icon: 'audio-lines',
    sabins: [0.02, 0.02, 0.01, 0.01, 0.01, 0.01],
    dims: [1.4, 1.1, 0.3],
    color: 0x2a2a40,
    wireColor: 0x709424,
    placement: 'floor',
    isMonitor: true,
  },
  {
    id: 'subwoofer',
    label: 'Subwoofer',
    icon: 'box',
    sabins: [0.1, 0.15, 0.1, 0.05, 0.03, 0.02],
    dims: [0.4, 0.4, 0.45],
    color: 0x1a1a30,
    wireColor: 0x9333ea,
    placement: 'floor',
  },

  /* ─── Acoustic treatment ─────────────────────────────────────────── */
  {
    id: 'foam-panel',
    label: 'Foam Panel',
    icon: 'grid-3x3',
    sabins: [0.1, 0.3, 0.8, 1.0, 0.95, 0.9],
    dims: [1.0, 1.0, 0.05],
    color: 0xc05050,
    wireColor: 0xff7070,
    placement: 'wall',
  },
  {
    id: 'bass-trap',
    label: 'Bass Trap',
    icon: 'triangle',
    sabins: [1.5, 2.0, 2.5, 2.0, 1.5, 1.0],
    dims: [0.3, 2.0, 0.3],
    color: 0x5050c0,
    wireColor: 0x7070ff,
    placement: 'corner',
  },
  {
    id: 'curtain',
    label: 'Heavy Curtain',
    icon: 'blinds',
    sabins: [0.28, 0.7, 1.1, 1.44, 1.4, 1.3],
    dims: [1.5, 2.2, 0.05],
    color: 0xa06080,
    wireColor: 0xd090b0,
    placement: 'wall',
  },
  {
    id: 'rug',
    label: 'Area Rug',
    icon: 'rectangle-horizontal',
    sabins: [0.04, 0.12, 0.28, 0.74, 1.2, 1.3],
    dims: [2.0, 0.02, 1.5],
    color: 0x9050a0,
    wireColor: 0xc080d0,
    placement: 'floor',
  },

  /* ─── Furniture ──────────────────────────────────────────────────── */
  {
    id: 'desk',
    label: 'Desk',
    icon: 'monitor',
    sabins: [0.2, 0.3, 0.5, 0.7, 0.6, 0.6],
    dims: [1.2, 0.75, 0.6],
    color: 0x5a7a5a,
    wireColor: 0x80aa80,
    placement: 'floor',
  },
  {
    id: 'chair',
    label: 'Chair',
    icon: 'armchair',
    sabins: [0.3, 0.5, 1.0, 1.5, 1.3, 1.3],
    dims: [0.6, 0.8, 0.6],
    color: 0x8b7050,
    wireColor: 0xbba070,
    placement: 'floor',
  },
  {
    id: 'sofa',
    label: 'Sofa',
    icon: 'sofa',
    sabins: [1.0, 1.8, 3.5, 5.0, 4.5, 4.5],
    dims: [2.0, 0.8, 0.8],
    color: 0x7a6060,
    wireColor: 0xaa8888,
    placement: 'floor',
  },
  {
    id: 'bookshelf',
    label: 'Bookshelf',
    icon: 'library',
    sabins: [0.5, 1.0, 1.5, 2.5, 2.0, 2.0],
    dims: [1.0, 2.0, 0.3],
    color: 0x6a5a3a,
    wireColor: 0x9a8a60,
    placement: 'floor',
  },
  {
    id: 'single-bed',
    label: 'Single Bed',
    icon: 'bed-single',
    sabins: [0.9, 1.6, 3.2, 4.0, 3.8, 3.8],
    dims: [2.0, 0.5, 1.0],
    color: 0xc2956c,
    wireColor: 0xe0b080,
    placement: 'floor',
  },
  {
    id: 'double-bed',
    label: 'Double Bed',
    icon: 'bed-double',
    sabins: [1.5, 2.8, 5.6, 7.0, 6.6, 6.6],
    dims: [2.0, 0.5, 1.5],
    color: 0xa07850,
    wireColor: 0xd4a070,
    placement: 'floor',
  },
  {
    id: 'closet',
    label: 'Closet',
    icon: 'shirt',
    sabins: [1.0, 1.5, 3.0, 4.0, 3.5, 3.5],
    dims: [1.5, 2.0, 0.6],
    color: 0x6060a0,
    wireColor: 0x9090d0,
    placement: 'floor',
  },

  /* ─── Room elements ──────────────────────────────────────────────── */
  {
    id: 'door-solid',
    label: 'Solid Door',
    icon: 'door-open',
    sabins: [0.14, 0.1, 0.06, 0.08, 0.1, 0.1],
    dims: [0.9, 2.1, 0.05],
    color: 0x6b4226,
    wireColor: 0x9a7050,
    placement: 'wall',
    isDoor: true,
  },
  {
    id: 'door-hollow',
    label: 'Hollow Door',
    icon: 'door-closed',
    sabins: [0.3, 0.25, 0.15, 0.1, 0.1, 0.08],
    dims: [0.9, 2.1, 0.05],
    color: 0x8b7355,
    wireColor: 0xbba580,
    placement: 'wall',
    isDoor: true,
  },
  {
    id: 'window',
    label: 'Window',
    icon: 'app-window',
    sabins: [0.35, 0.25, 0.18, 0.12, 0.07, 0.04],
    dims: [1.5, 1.2, 0.05],
    color: 0x80c0d0,
    wireColor: 0xa0e0f0,
    placement: 'wall',
  },
] as const;

/** IDs whose default placement is on a wall (informational / UI helper). */
export const WALL_MOUNT_IDS: readonly PaletteObjectId[] = PALETTE_OBJECTS.filter(
  (o) => o.placement === 'wall',
).map((o) => o.id);

/** IDs placed in corners (bass traps). */
export const CORNER_MOUNT_IDS: readonly PaletteObjectId[] = PALETTE_OBJECTS.filter(
  (o) => o.placement === 'corner',
).map((o) => o.id);

/** Logical grouping for rendering the palette panel with sub-headings. */
export const PALETTE_GROUPS: readonly {
  title: string;
  ids: readonly PaletteObjectId[];
}[] = [
  {
    title: 'Sound sources',
    ids: ['studio-monitors', 'monitor-stands', 'subwoofer'],
  },
  {
    title: 'Acoustic treatment',
    ids: ['foam-panel', 'bass-trap', 'curtain', 'rug'],
  },
  {
    title: 'Furniture',
    ids: ['desk', 'chair', 'sofa', 'bookshelf', 'single-bed', 'double-bed', 'closet'],
  },
  {
    title: 'Room elements',
    ids: ['door-solid', 'door-hollow', 'window'],
  },
];

export function getPaletteObject(id: PaletteObjectId): PaletteObject | undefined {
  return PALETTE_OBJECTS.find((o) => o.id === id);
}

export function requirePaletteObject(id: PaletteObjectId): PaletteObject {
  const o = getPaletteObject(id);
  if (!o) throw new Error(`Unknown palette object: ${id}`);
  return o;
}
